/**
 * urssaf.js — Helper pour le système de cotisations URSSAF (08/02/2026).
 *
 * Pédagogique only : la masse salariale (employerCost daily) inclut DÉJÀ
 * les cotisations sociales dans le modèle financier — on ne re-prélève
 * RIEN à la déclaration trimestrielle. Le but de ce module est de
 * matérialiser le rythme administratif réel français :
 *
 *   1. Reminder envoyé 15 jours avant l'échéance trimestrielle
 *      (jours 15 de mars, juin, septembre, décembre).
 *   2. Déclaration officialisée le 1er du mois suivant
 *      (jours 1 d'avril, juillet, octobre, janvier) avec montant calculé.
 *   3. Historique consultable dans l'onglet Fiscalité (urssafHistory).
 *
 * Ratio URSSAF utilisé : 42 % de l'employerCost (charges patronales +
 * salariales typiques en France pour un chauffeur ou employé qualifié).
 */

import { DRIVERS } from '../../data/drivers';
import { SUPPORT_ROLES } from '../../data/supportRoles';

// Part de l'employerCost qui correspond à des cotisations URSSAF
// (≈ 42 %) — le reste = salaire net + impôt à la source.
export const URSSAF_RATE = 0.42;

/**
 * Compute the quarterly URSSAF estimate (informational) from the current
 * payroll snapshot. Aggregates drivers + hired support roles.
 *
 * @param {object} state — gameStore snapshot
 * @returns {{ monthlyPayroll: number, quarterlyUrssaf: number, headcount: number }}
 */
export function computeUrssafEstimate(state) {
  const drivers = state.drivers || [];
  const hiredSupport = state.hiredSupport || {};

  const monthlyDriverCost = drivers.reduce((sum, d) => {
    const data = DRIVERS.find(dd => dd.id === d.dataId);
    return sum + (data?.employerCost || 0);
  }, 0);

  const monthlySupportCost = SUPPORT_ROLES.reduce((sum, role) => {
    if (!hiredSupport[role.id]) return sum;
    return sum + (role.monthlyCost || 0);
  }, 0);

  const monthlyPayroll = monthlyDriverCost + monthlySupportCost;
  const quarterlyUrssaf = Math.round(monthlyPayroll * 3 * URSSAF_RATE);
  const headcount = drivers.length + Object.keys(hiredSupport).filter(k => hiredSupport[k]).length;

  return { monthlyPayroll, quarterlyUrssaf, headcount };
}

/**
 * Quarter index for a given month (1..12).
 * Month 1-3 → Q1, 4-6 → Q2, 7-9 → Q3, 10-12 → Q4.
 */
export function quarterFromMonth(month) {
  return Math.ceil(month / 3);
}

/**
 * Quarter label "T1 2026" formatted from a month/year pair (the
 * declaration month — i.e. month following the quarter end).
 */
export function quarterLabelFromDeclarationMonth(month, year) {
  // Declaration months: 1 (T4 of previous year), 4 (T1), 7 (T2), 10 (T3)
  if (month === 1) return { quarter: 4, year: year - 1, label: `T4 ${year - 1}` };
  if (month === 4) return { quarter: 1, year, label: `T1 ${year}` };
  if (month === 7) return { quarter: 2, year, label: `T2 ${year}` };
  if (month === 10) return { quarter: 3, year, label: `T3 ${year}` };
  return null;
}

/**
 * True if the current state.month + state.day matches a reminder window
 * (day 15 of a quarter-end month: mars/juin/sept/déc).
 */
export function isUrssafReminderDay(month, day) {
  return day === 15 && [3, 6, 9, 12].includes(month);
}

/**
 * True if the current state.month + state.day matches a declaration
 * window (day 1 of a post-quarter month: jan/avr/juil/oct).
 */
export function isUrssafDeclarationDay(month, day) {
  return day === 1 && [1, 4, 7, 10].includes(month);
}
